import React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {MenuOption} from '@spaced-out/ui-design-system';
import {useSelector, useDispatch} from 'react-redux';
import {getTimezone} from 'src/selectors/date-time';
import {useHistory} from 'src/rerouter';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {useTRMEnabled, useTrmAppShellEnabled} from 'src/hooks/product-flags';
import {
  selectNotifications,
  selectHasMoreNotifications,
  selectCursor,
} from 'src/selectors/notifications';
import {groupNotificationsByDate} from 'src/components/notifications/utils';
import {getNotifications} from 'src/action-creators/notifications';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  PanelHeader,
  PanelBody,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {SubTitleLarge} from '@spaced-out/ui-design-system/lib/components/Text';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import NotificationSection from 'src/components/notifications/notification-section';
import useScrollLoader from 'src/hooks/useScrollLoader';
import css from './notification-panel-content.css';


export const NotificationPanelContent = ({
  onClose,
  filters,
}: {
  onClose: () => void;
  filters: string[];
}) => {
  const {t} = useLabelTranslation();
  const timezone = useSelector(getTimezone) || '';
  const router = useHistory();
  const dispatch = useDispatch();
  const trmEnabled = useTRMEnabled();
  const isTrmAppShellEnabled = useTrmAppShellEnabled();
  const showNotificationSettingsIcon = trmEnabled && isTrmAppShellEnabled;
  const {changeLocation} = useWebAndExtRouterControl();
  const {isExtension} = useEnvironmentContext();
  const unreadLabel = t('Unread', 'Unread');
  const allLabel = t('All', 'All');
  const [selectedTab, setSelectedTab] = React.useState({
    tabId: 'unread',
    label: unreadLabel,
  });
  const [loading, setLoading] = React.useState(false);
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
  const handleSettingsClick = () => {
    router.push('/trm#/settings/account/subscription');
    changeLocation(router, '/trm#/settings/account/subscription');
    onClose();
  };

  const notifications = useSelector(selectNotifications);
  const selectedNotifications =
    selectedTab.tabId === 'unread'
      ? notifications.filter((notification) => !notification.readAt)
      : notifications;
  const [initialLoad, setInitialLoad] = React.useState(true);
  const displayNotifications = React.useMemo(() => {
    return groupNotificationsByDate(timezone, selectedNotifications);
  }, [selectedNotifications, selectedTab.tabId, timezone]);
  const hasMore = useSelector(selectHasMoreNotifications);
  const nextCursor = useSelector(selectCursor);
  const fetchMoreNotifications = React.useCallback(async () => {
    if (!nextCursor || loading) {
      return;
    }
    setLoading(true);
    try {
      await dispatch(
        getNotifications({
          appendData: true,
          source: selectedKeys.join(','),
          cursor: nextCursor,
          view: selectedTab.tabId === 'unread' ? 'unread' : 'all',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, nextCursor, selectedKeys, selectedTab.tabId, loading]);

  const fetchNotifications = async () => {
    setInitialLoad(true);
    await dispatch(
      getNotifications({
        appendData: false,
        source: selectedKeys.join(','),
        view: selectedTab.tabId === 'unread' ? 'unread' : 'all',
      }),
    );
    setInitialLoad(false);
  };

  React.useEffect(() => {
    fetchNotifications();
  }, [selectedKeys, selectedTab.tabId]);

  React.useEffect(() => {
    if (selectedTab.tabId === 'unread' && selectedNotifications.length < 10) {
      fetchMoreNotifications();
    }
  }, [selectedNotifications, selectedTab.tabId]);

  const [containerRef, loaderRef, handleMovement] = useScrollLoader({
    onLoadMore: fetchMoreNotifications,
    loading,
    done: !hasMore,
  });

  return (
    <>
      <PanelHeader
        className={css.panelHeader}
        size="medium"
        onCloseButtonClick={onClose}
        hideCloseBtn={isExtension}
      >
        <div className={css.panelHeaderContent}>
          <SubTitleLarge>{t('Notifications', 'Notifications')}</SubTitleLarge>
          {!isExtension && showNotificationSettingsIcon && (
            <div className={css.panelHeaderActions}>
              <Tooltip
                elevation="modal"
                body={t('Notification_settings', 'Notification settings')}
              >
                <Button
                  onClick={handleSettingsClick}
                  type="ghost"
                  size="small"
                  iconLeftName="gear"
                />
              </Tooltip>
            </div>
          )}
        </div>
      </PanelHeader>
      <div className={css.panelSecondaryActions}>
        <div className={css.tabSlot}>
          <TabList
            onSelect={(tab) =>
              setSelectedTab({tabId: tab.tabId, label: tab.label || ''})
            }
            selectedTab={selectedTab}
            size="medium"
            testId="notification-panel-tabs"
          >
            <Tab label={unreadLabel} tabId="unread" />
            <Tab label={allLabel} tabId="all" />
          </TabList>
        </div>
        {filters.length > 0 && (
          <ButtonDropdown
            iconLeftName="bars-filter"
            type="ghost"
            menu={{
              isFluid: false,
              menuDisabled: false,
              footer: (
                <MenuFooter
                  selectedKeys={selectedKeys}
                  setSelectedKeys={setSelectedKeys}
                />
              ),
              options: filters.map((filter) => ({
                label: filter,
                value: filter,
                key: filter,
              })),
              selectedKeys,
              optionsVariant: 'checkbox',
              size: 'small',
            }}
            onOptionSelect={(option: MenuOption) => {
              if (selectedKeys.includes(option.key)) {
                setSelectedKeys((prev) =>
                  prev.filter((key) => key !== option.key),
                );
              } else {
                setSelectedKeys((prev) => [...prev, option.key]);
              }
            }}
            size="small"
            testId="button-dropdown-text-only-story"
          />
        )}
      </div>
      <PanelBody className={css.panelBody}>
        <div
          className={css.notifications}
          ref={containerRef as React.Ref<HTMLDivElement>}
          onScrollCapture={handleMovement}
        >
          {initialLoad && (
            <CircularLoader size="large" className={css.loader} />
          )}
          {!initialLoad && (
            <NotificationSection
              displayNotifications={displayNotifications}
              removeNotification={selectedTab.tabId === 'unread'}
            />
          )}
          <div ref={loaderRef as React.Ref<HTMLDivElement>}>
            {loading && <CircularLoader className={css.loader} />}
          </div>
        </div>
      </PanelBody>
    </>
  );
};

const MenuFooter = ({
  selectedKeys,
  setSelectedKeys,
}: {
  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;
}) => {
  const {t} = useLabelTranslation();
  const handleClear = () => {
    setSelectedKeys([]);
  };
  return (
    <Button
      onClick={handleClear}
      type="ghost"
      isFluid={true}
      size="small"
      disabled={selectedKeys.length === 0}
    >
      {t('Clear_Filters', 'Clear Filters')}
    </Button>
  );
};
